import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Flex } from 'rebass';
import { Textarea } from '@rebass/forms';
import Modal, { ModalBody, ModalFooter } from '../../../../components/Modal';
import Button from '../../../../components/Button/Button';
import useNotify from '../../../../hooks/useNotify';
import { BULK_UPDATE_HAZARD_SURVEY_STATUS } from '@/components/WaterHazard/queries';
import { HazardSurveyStatusPicker } from '@/components/WaterHazard/HazardSurveyStatusPicker';
import { HazardSurveyStatus } from '@/services/hazardSurvey';

type BulkSurveyStatusModalProps = {
  jobIds: string[] | readonly string[];
  onClose: (actionTaken?: boolean) => void;
};

const BulkSurveyStatusModal: React.FC<BulkSurveyStatusModalProps> = ({
  jobIds,
  onClose,
}) => {
  const { showError } = useNotify();
  const [status, setStatus] = useState<HazardSurveyStatus | null>(null);
  const [comment, setComment] = useState('');

  const [bulkUpdate, { loading }] = useMutation(
    BULK_UPDATE_HAZARD_SURVEY_STATUS,
    {
      onCompleted: () => onClose(true),
      onError: useCallback((error: any) => {
        showError('Error updating survey status', error);
      }, []),
    }
  );

  const handleConfirm = useCallback(() => {
    if (!status) return;
    bulkUpdate({
      variables: {
        jobIds: [...jobIds],
        status,
        comment: comment || undefined,
      },
    });
  }, [bulkUpdate, jobIds, status, comment]);

  return (
    <Modal
      title={`Set Status — ${jobIds.length} Survey${jobIds.length !== 1 ? 's' : ''}`}
      onClose={() => onClose()}
    >
      <ModalBody>
        <Flex p={3} flexDirection="column" sx={{ gap: 3 }}>
          <HazardSurveyStatusPicker
            value={status}
            onChange={(v) => setStatus(v ?? null)}
            isMulti={false}
            placeholder="Select new status…"
          />
          <Textarea
            height={3 * 16 + 2}
            placeholder="Optional comment"
            value={comment}
            onChange={(e: any) => setComment(e.target.value)}
          />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="check-circle"
          disabled={!status || loading}
          onClick={handleConfirm}
        >
          Apply
        </Button>
        <Button
          ml={3}
          variant="outline"
          icon="slash-circle"
          onClick={() => onClose()}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkSurveyStatusModal;
